import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import {
  Eye,
  EyeOff,
  Sticker,
  Tag,
  ShoppingBag,
  Boxes,
  Sparkles,
  Star,
  Gem,
} from "lucide-react";
import { signInRequest, registerInitiateRequest } from "../../redux/auth/auth.actions";
import { LOGIN, SIGNUP, SIGNUP_PROCESS } from "../../routes/routes";

const PageWrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ededed;
  padding: 2rem 1rem;

  @media (max-width: 480px) {
    padding: 0.75rem 0.5rem;
  }
`;

const Container = styled.div`
  position: relative;
  width: 100%;
  max-width: 900px;
  min-height: 550px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  overflow: hidden;

  @media (max-width: 768px) {
    min-height: 0;
    border-radius: 12px;
  }

  @media (max-width: 480px) {
    border-radius: 8px;
  }
`;

const FormsWrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;

  @media (max-width: 768px) {
    display: none;
  }
`;

const FormPane = styled.div`
  width: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 3.5rem;
  transition: opacity 0.4s ease-in-out 0.15s, visibility 0.4s ease-in-out 0.15s;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};

  @media (max-width: 900px) {
    padding: 2.5rem 2rem;
  }
`;

const FormInner = styled.div`
  width: 100%;
  max-width: 340px;
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  margin-bottom: 1.75rem;
`;

const LogoMark = styled.div`
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  background: #001c49;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.05rem;
  font-weight: 700;
  color: #ffffff;
`;

const LogoWordmark = styled.span`
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2937;
`;

const Title = styled.h1`
  color: #1f2937;
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0 0 1.25rem 0;
  text-align: center;

  @media (max-width: 480px) {
    font-size: 1.4rem;
    margin-bottom: 1rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  background: #f3f4f6;
  color: #1f2937;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px #001c49;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.6rem;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #374151;
  }
`;

const ForgotLink = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  font-size: 0.8rem;
  text-align: center;
  cursor: pointer;
  margin: 0.5rem 0 0.5rem 0;

  &:hover {
    color: #001c49;
    text-decoration: underline;
  }
`;

const SubmitButton = styled.button`
  width: 50%;
  align-self: center;
  background: linear-gradient(180deg, #0a3d7a, #001c49);
  color: white;
  border: none;
  padding: 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 0.5rem;

  &:hover:not(:disabled) {
    background: linear-gradient(180deg, #001c49, #00122f);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    width: 70%;
  }

  @media (max-width: 380px) {
    width: 100%;
  }
`;

const SuccessMessage = styled.div`
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  color: #166534;
  font-size: 0.8rem;
  text-align: center;
`;

const SuccessLink = styled.button`
  background: none;
  border: none;
  color: #166534;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
`;

const OverlayContainer = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  width: 50%;
  height: 100%;
  overflow: hidden;
  transition: transform 0.6s ease-in-out;
  transform: ${({ $isSignUp }) => ($isSignUp ? "translateX(-100%)" : "translateX(0)")};
  z-index: 10;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Overlay = styled.div`
  background: linear-gradient(135deg, #10306b 0%, #062354 50%, #001c49 100%);
  position: relative;
  left: -100%;
  height: 100%;
  width: 200%;
  transform: ${({ $isSignUp }) => ($isSignUp ? "translateX(50%)" : "translateX(0)")};
  transition: transform 0.6s ease-in-out;
  color: white;
`;

const OverlayPanel = styled.div`
  position: absolute;
  top: 0;
  width: 50%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 3rem 2.5rem;
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 900px) {
    padding: 2.5rem 1.75rem;
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(var(--rot, 0deg)); }
  50% { transform: translateY(-14px) rotate(var(--rot, 0deg)); }
`;

const pulseRing = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.35); }
  70% { box-shadow: 0 0 0 14px rgba(255, 255, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
`;

const FloatingIcon = styled.div`
  position: absolute;
  top: ${(props) => props.$top};
  left: ${(props) => props.$left};
  color: rgba(255, 255, 255, ${(props) => props.$opacity || 0.18});
  --rot: ${(props) => props.$rotate || "0deg"};
  animation: ${float} ${(props) => props.$duration || "5s"} ease-in-out infinite;
  animation-delay: ${(props) => props.$delay || "0s"};
  pointer-events: none;
`;

const IconBadge = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.12);
  border: 1.5px solid rgba(255, 255, 255, 0.35);
  margin-bottom: 1.25rem;
  animation: ${pulseRing} 2.6s ease-out infinite;
`;

const cardFloat = keyframes`
  0%, 100% { transform: translateY(0) rotate(var(--rot, 0deg)); }
  50% { transform: translateY(-10px) rotate(calc(var(--rot, 0deg) + 3deg)); }
`;

const cardWobble = keyframes`
  0%, 100% { transform: rotateZ(-6deg) rotateY(0deg); }
  50% { transform: rotateZ(6deg) rotateY(12deg); }
`;

const MiniCard = styled.div`
  position: absolute;
  top: ${(props) => props.$top};
  left: ${(props) => props.$left};
  width: 30px;
  height: 42px;
  border-radius: 5px;
  --rot: ${(props) => props.$rotate || "0deg"};
  background: linear-gradient(
    155deg,
    rgba(255, 255, 255, 0.28) 0%,
    rgba(255, 255, 255, 0.08) 60%
  );
  border: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  animation: ${cardFloat} ${(props) => props.$duration || "6s"} ease-in-out infinite;
  animation-delay: ${(props) => props.$delay || "0s"};
  pointer-events: none;

  &::before {
    content: "";
    position: absolute;
    inset: 3px;
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 3px;
  }
`;

const CardStack = styled.div`
  position: relative;
  width: 66px;
  height: 92px;
  margin-bottom: 1.5rem;
  perspective: 600px;
`;

const StackedCard = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 10px;
  background: linear-gradient(155deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.04) 100%);
  border: 1.5px solid rgba(255, 255, 255, 0.25);
  transform: rotate(${(props) => props.$rotate || "0deg"});
`;

const FrontCard = styled(StackedCard)`
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.14);
  border: 1.5px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.3);
  transform-style: preserve-3d;
  animation: ${cardWobble} 3.4s ease-in-out infinite;
`;

const OverlayLeft = styled(OverlayPanel)`
  left: 0;
  transform: ${({ $isSignUp }) => ($isSignUp ? "translateX(0)" : "translateX(-20%)")};
  transition: transform 0.6s ease-in-out;
`;

const OverlayRight = styled(OverlayPanel)`
  right: 0;
  transform: ${({ $isSignUp }) => ($isSignUp ? "translateX(20%)" : "translateX(0)")};
  transition: transform 0.6s ease-in-out;
`;

const OverlayTitle = styled.h2`
  font-size: 1.7rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
`;

const OverlayText = styled.p`
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0 0 1.5rem 0;
  opacity: 0.9;
`;

const GhostButton = styled.button`
  background: transparent;
  border: 2px solid #ffffff;
  color: #ffffff;
  border-radius: 20px;
  padding: 0.65rem 2.5rem;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const MobileToggle = styled.div`
  display: none;
  text-align: center;
  padding: 1rem 0 0.5rem 0;
  font-size: 0.875rem;
  color: #6b7280;

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileToggleLink = styled.button`
  background: none;
  border: none;
  color: #001c49;
  font-weight: 600;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const MobileFormWrapper = styled.div`
  display: none;
  padding: 2.5rem 1.5rem 1.5rem 1.5rem;

  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
  }

  @media (max-width: 380px) {
    padding: 1.75rem 1rem 0.75rem 1rem;
  }
`;

function AuthPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading } = useSelector((state) => state.auth);

  const [isSignUp, setIsSignUp] = useState(location.pathname === SIGNUP);
  const [showPassword, setShowPassword] = useState(false);

  // Keep the sign in / sign up state in sync with the URL. AuthPage is
  // reused for both /login and /signup (same component instance), so the
  // useState initializer above only runs once on first mount and won't
  // re-run on subsequent navigations between the two routes.
  useEffect(() => {
    setIsSignUp(location.pathname === SIGNUP);
  }, [location.pathname]);

  const [loginData, setLoginData] = useState({ username: "", password: "" });
  const [email, setEmail] = useState("");
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [verificationToken, setVerificationToken] = useState("");

  const goTo = (signUp) => {
    setIsSignUp(signUp);
    navigate(signUp ? SIGNUP : LOGIN, { replace: true });
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLoginSubmit = (e) => {
    e.preventDefault();
    dispatch(
      signInRequest({
        formValues: loginData,
        func: () => navigate("/dashboard"),
      })
    );
  };

  const handleVerifyEmail = (e) => {
    e.preventDefault();
    if (!email) return;

    dispatch(
      registerInitiateRequest({
        formValues: { email },
        func: (response) => {
          if (response?.verification_token) {
            setVerificationToken(response.verification_token);
            setVerificationSuccess(true);
          }
        },
      })
    );
  };

  const loginForm = (
    <FormInner>
      <LogoRow>
        <LogoMark>SC</LogoMark>
        <LogoWordmark>Sticker Checker</LogoWordmark>
      </LogoRow>
      <Title>Sign in</Title>

      <Form onSubmit={handleLoginSubmit}>
        <Input
          type="text"
          name="username"
          value={loginData.username}
          onChange={handleLoginChange}
          required
          placeholder="Username"
        />
        <PasswordInputWrapper>
          <Input
            type={showPassword ? "text" : "password"}
            name="password"
            value={loginData.password}
            onChange={handleLoginChange}
            required
            placeholder="Password"
          />
          <PasswordToggle
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </PasswordToggle>
        </PasswordInputWrapper>

        <ForgotLink type="button" onClick={() => navigate("/forgot-password")}>
          Forgot your password?
        </ForgotLink>

        <SubmitButton type="submit" disabled={loading}>
          {loading ? "Signing in..." : "Sign in"}
        </SubmitButton>
      </Form>
    </FormInner>
  );

  const signupForm = (
    <FormInner>
      <LogoRow>
        <LogoMark>SC</LogoMark>
        <LogoWordmark>Sticker Checker</LogoWordmark>
      </LogoRow>
      <Title>Create Account</Title>

      <Form onSubmit={handleVerifyEmail}>
        <Input
          type="email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="Email"
        />

        <SubmitButton type="submit" disabled={loading}>
          {loading ? "Sending..." : "Sign up"}
        </SubmitButton>
      </Form>

      {verificationSuccess && verificationToken && (
        <SuccessMessage>
          Verification link has been sent to your email!{" "}
          <SuccessLink
            type="button"
            onClick={() => navigate(`${SIGNUP_PROCESS}?token=${verificationToken}`)}
          >
            Click here to continue →
          </SuccessLink>
        </SuccessMessage>
      )}
    </FormInner>
  );

  return (
    <PageWrapper>
      <Container>
        <FormsWrapper>
          <FormPane $visible={!isSignUp}>{loginForm}</FormPane>
          <FormPane $visible={isSignUp}>{signupForm}</FormPane>
        </FormsWrapper>

        <OverlayContainer $isSignUp={isSignUp}>
          <Overlay $isSignUp={isSignUp}>
            <OverlayLeft $isSignUp={isSignUp}>
              <MiniCard $top="12%" $left="16%" $duration="6s" $rotate="-14deg">
                <Star size={12} />
              </MiniCard>
              <MiniCard $top="66%" $left="10%" $duration="7s" $delay="0.6s" $rotate="10deg">
                <Gem size={12} />
              </MiniCard>
              <FloatingIcon $top="18%" $left="74%" $duration="5.5s" $delay="1.1s" $rotate="6deg">
                <Tag size={20} />
              </FloatingIcon>
              <FloatingIcon $top="72%" $left="76%" $duration="6.4s" $delay="0.3s" $rotate="-8deg">
                <Sparkles size={18} />
              </FloatingIcon>

              <CardStack>
                <StackedCard $rotate="-10deg" />
                <StackedCard $rotate="8deg" />
                <FrontCard>
                  <Sticker size={26} />
                </FrontCard>
              </CardStack>
              <OverlayTitle>Welcome Back!</OverlayTitle>
              <OverlayText>
                Already have an account? Sign in to keep using Sticker
                Checker.
              </OverlayText>
              <GhostButton type="button" onClick={() => goTo(false)}>
                Sign in
              </GhostButton>
            </OverlayLeft>
            <OverlayRight $isSignUp={isSignUp}>
              <MiniCard $top="14%" $left="14%" $duration="6.5s" $rotate="12deg">
                <Gem size={12} />
              </MiniCard>
              <MiniCard $top="68%" $left="78%" $duration="5.8s" $delay="0.4s" $rotate="-10deg">
                <Star size={12} />
              </MiniCard>
              <FloatingIcon $top="20%" $left="76%" $duration="6.2s" $delay="0.9s" $rotate="-6deg">
                <Boxes size={20} />
              </FloatingIcon>
              <FloatingIcon $top="70%" $left="14%" $duration="5.6s" $delay="0.2s" $rotate="10deg">
                <ShoppingBag size={18} />
              </FloatingIcon>

              <CardStack>
                <StackedCard $rotate="-8deg" />
                <StackedCard $rotate="10deg" />
                <FrontCard>
                  <ShoppingBag size={24} />
                </FrontCard>
              </CardStack>
              <OverlayTitle>Hello, Friend!</OverlayTitle>
              <OverlayText>
                Enter your email and start your journey with us today.
              </OverlayText>
              <GhostButton type="button" onClick={() => goTo(true)}>
                Sign up
              </GhostButton>
            </OverlayRight>
          </Overlay>
        </OverlayContainer>

        {/* Mobile fallback: no slide animation, simple toggle */}
        <MobileFormWrapper>
          {isSignUp ? signupForm : loginForm}
        </MobileFormWrapper>
        <MobileToggle>
          {isSignUp ? (
            <>
              Already have an account?{" "}
              <MobileToggleLink type="button" onClick={() => goTo(false)}>
                Sign in
              </MobileToggleLink>
            </>
          ) : (
            <>
              Don't have an account?{" "}
              <MobileToggleLink type="button" onClick={() => goTo(true)}>
                Sign up
              </MobileToggleLink>
            </>
          )}
        </MobileToggle>
      </Container>
    </PageWrapper>
  );
}

export default AuthPage;
